import { useQuery, useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { AuditService } from '@/services/audit.service';
import { QUERY_KEYS } from '@/constants';
import { AuditLogFilters } from '@/types';
import { handleApiError } from '@/services/api';

/**
 * Hook for fetching recent activities for the current user
 * Used by the dashboard activity feed
 */
export const useRecentActivities = (limit: number = 5) => {
  return useQuery({
    queryKey: ['recentActivities', limit],
    queryFn: () => AuditService.getRecentActivities(limit),
    staleTime: 2 * 60 * 1000, // 2 minutes - activities are more dynamic
    refetchOnWindowFocus: true, // Refresh when user returns to tab
    refetchInterval: false, // Disable polling
  });
};

/**
 * Hook for fetching paginated audit logs with filters
 * Admin only - requires SYSTEM_ADMIN or SUPER_ADMIN role
 */
export const useAuditLogs = (filters: AuditLogFilters = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.AUDIT_LOGS, filters],
    queryFn: () => AuditService.getAuditLogs(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes - audit logs don't change retroactively
    refetchOnWindowFocus: false,
    refetchInterval: false, // Disable polling
  });
};

/**
 * Hook for fetching audit statistics
 */
export const useAuditStats = (startDate?: string, endDate?: string) => {
  return useQuery({
    queryKey: ['auditStats', startDate, endDate],
    queryFn: () => AuditService.getAuditStats(startDate, endDate),
    staleTime: 10 * 60 * 1000, // 10 minutes - stats change slowly
    refetchOnWindowFocus: false,
    refetchInterval: false, // Disable polling
  });
};

/**
 * Hook for exporting audit logs as CSV
 */
export const useExportAuditLogs = () => {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (filters: AuditLogFilters = {}) =>
      AuditService.exportAuditLogs(filters),
    onSuccess: (blob) => {
      // Create a download link for the CSV blob
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar('Audit logs exporterade', { variant: 'success' });
    },
    onError: (error) => {
      const errorMessage = handleApiError(error);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};
